<template>
  <v-container fluid>
    <v-data-table
      :headers="headers"
      :items="parentCategories"
      :search="search"
      :custom-filter="filterCategory"
      :loading="loading"
      sort-by="name"
      class="elevation-1"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>Categorías y sub-categorías</v-toolbar-title>
          <v-chip small class="ml-3" color="primary" outlined>
            {{ parentCategories.length }} categorías · {{ totalSubCategories }} sub-categorías
          </v-chip>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            class="mt-2"
            label="Buscar categoría o sub-categoría"
            prepend-inner-icon="mdi-magnify"
            clearable
            hide-details
            dense
            outlined
            style="max-width: 400px"
          ></v-text-field>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="openNewCategory">
            <v-icon left>add</v-icon>
            Nueva Categoría
          </v-btn>
        </v-toolbar>
      </template>

      <template v-slot:[`item.name`]="{ item }">
        {{ item.name }}
        <div v-if="search && matchingSubNames(item).length" class="text-caption grey--text">
          Contiene: {{ matchingSubNames(item).join(", ") }}
        </div>
      </template>

      <template v-slot:[`item.sub_count`]="{ item }">
        <v-chip small :color="subCount(item.id) ? 'blue lighten-5' : 'grey lighten-3'">
          {{ subCount(item.id) }}
        </v-chip>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" color="blue" class="mr-2" @click="openCategory(item)">
              mdi-pencil
            </v-icon>
          </template>
          <span>Editar / ver sub-categorías</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-if="item.id != 1" v-bind="attrs" v-on="on" color="red" @click="askDelete(item)">
              mdi-delete
            </v-icon>
          </template>
          <span>Eliminar</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <!-- Nueva categoría -->
    <v-dialog v-model="dialogNew" max-width="500px">
      <v-card>
        <v-card-title class="text-h6">
          Nueva categoría
          <v-spacer></v-spacer>
          <v-icon @click="dialogNew = false">close</v-icon>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-text-field
            v-model="newCategoryName"
            label="Nombre"
            outlined
            dense
            autofocus
            @keyup.enter="createCategory"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogNew = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="createCategory">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Editar categoría y sus sub-categorías -->
    <v-dialog v-model="dialog" max-width="900px" scrollable>
      <v-card v-if="selected">
        <v-card-title class="text-h6">
          {{ selected.name }}
          <v-chip small class="ml-3" color="primary" outlined>
            {{ subCategories.length }} sub-categorías
          </v-chip>
          <v-spacer></v-spacer>
          <v-icon @click="dialog = false">mdi-close</v-icon>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pt-4">
          <v-row dense align="center">
            <v-col cols="12" md="8">
              <v-text-field
                v-model="categoryName"
                label="Nombre de la categoría"
                outlined
                dense
                hide-details
                @keyup.enter="renameCategory"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-btn
                block
                color="primary"
                outlined
                :disabled="!categoryName || categoryName.trim() === selected.name"
                :loading="saving"
                @click="renameCategory"
              >
                Guardar nombre
              </v-btn>
            </v-col>
          </v-row>

          <v-subheader class="px-0 mt-4">Sub-categorías</v-subheader>

          <v-row dense align="center">
            <v-col cols="12" md="8">
              <v-text-field
                v-model="newSubName"
                label="Nueva sub-categoría"
                outlined
                dense
                hide-details
                @keyup.enter="createSubCategory"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-btn block color="primary" :loading="saving" :disabled="!newSubName" @click="createSubCategory">
                <v-icon left>add</v-icon> Agregar
              </v-btn>
            </v-col>
          </v-row>

          <v-text-field
            v-model="subSearch"
            class="mt-4"
            label="Buscar sub-categoría"
            prepend-inner-icon="mdi-magnify"
            clearable
            outlined
            dense
            hide-details
          ></v-text-field>

          <v-data-table
            :headers="subHeaders"
            :items="subCategories"
            :search="subSearch"
            :options.sync="subOptions"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
            sort-by="name"
            dense
            class="mt-2"
            no-data-text="Esta categoría no tiene sub-categorías"
            no-results-text="No hay sub-categorías que coincidan"
          >
            <template v-slot:[`item.index`]="{ index }">
              <span class="grey--text">{{ rowNumber(index) }}</span>
            </template>
            <template v-slot:[`item.name`]="{ item }">
              <v-edit-dialog large save-text="Guardar" cancel-text="Cancelar" @open="editSubName = item.name"
                @save="renameSubCategory(item)">
                {{ item.name }}
                <v-icon x-small class="ml-1">mdi-pencil</v-icon>
                <template v-slot:input>
                  <v-text-field v-model="editSubName" label="Nombre" single-line autofocus></v-text-field>
                </template>
              </v-edit-dialog>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-icon small color="red" @click="askDelete(item)">mdi-delete</v-icon>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Confirmar eliminación -->
    <v-dialog v-model="dialogDelete" max-width="500px">
      <v-card v-if="toDelete">
        <v-card-title class="text-h6">
          ¿Eliminar {{ toDelete.parent_id ? "la sub-categoría" : "la categoría" }} "{{ toDelete.name }}"?
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogDelete = false">Cancelar</v-btn>
          <v-btn color="red" dark :loading="saving" @click="deleteConfirm">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: "CategoriesAccountig",
  data() {
    return {
      loading: false,
      saving: false,
      search: "",
      subSearch: "",
      subOptions: { page: 1, itemsPerPage: 10 },
      categories: [],
      headers: [
        { text: "Código", value: "id", width: 120 },
        { text: "Nombre", value: "name" },
        { text: "Sub-categorías", value: "sub_count", sortable: false, width: 160 },
        { text: "Acciones", value: "actions", sortable: false, width: 120 },
      ],
      subHeaders: [
        { text: "#", value: "index", sortable: false, width: 60 },
        { text: "ID", value: "id", width: 100 },
        { text: "Nombre", value: "name" },
        { text: "", value: "actions", sortable: false, width: 60 },
      ],
      dialogNew: false,
      newCategoryName: "",
      dialog: false,
      selected: null,
      categoryName: "",
      newSubName: "",
      editSubName: "",
      dialogDelete: false,
      toDelete: null,
    };
  },
  created() {
    this.init();
  },
  computed: {
    parentCategories() {
      return this.categories.filter((c) => c.parent_id == null);
    },
    totalSubCategories() {
      return this.categories.length - this.parentCategories.length;
    },
    subCategories() {
      if (!this.selected) return [];
      return this.categories.filter((c) => c.parent_id == this.selected.id);
    },
  },
  methods: {
    async init() {
      this.loading = true;
      try {
        const response = await this.api_Get("accounting/categories_diary_book");
        this.categories = await response.json();
        // Mantener la categoría abierta sincronizada tras recargar
        if (this.selected) {
          this.selected = this.categories.find((c) => c.id == this.selected.id) || null;
        }
      } finally {
        this.loading = false;
      }
    },
    // Posición de la fila en la lista completa (ya ordenada/filtrada), no solo en la página actual
    rowNumber(index) {
      const { page, itemsPerPage } = this.subOptions;
      return itemsPerPage > 0 ? (page - 1) * itemsPerPage + index + 1 : index + 1;
    },
    subCount(id) {
      return this.categories.filter((c) => c.parent_id == id).length;
    },
    matchingSubNames(item) {
      const term = (this.search || "").toLowerCase();
      return this.categories
        .filter((c) => c.parent_id == item.id && String(c.name).toLowerCase().includes(term))
        .map((c) => c.name);
    },
    // La búsqueda principal también encuentra la categoría por el nombre de sus sub-categorías
    filterCategory(value, search, item) {
      const term = (search || "").toLowerCase();
      if (`${item.id} ${item.name}`.toLowerCase().includes(term)) return true;
      return this.matchingSubNames(item).length > 0;
    },
    nameExists(name, parentId, excludeId = null) {
      const n = name.trim().toLowerCase();
      return this.categories.some(
        (c) => (c.parent_id ?? null) == (parentId ?? null) && c.id != excludeId && String(c.name).trim().toLowerCase() === n
      );
    },
    openNewCategory() {
      this.newCategoryName = "";
      this.dialogNew = true;
    },
    openCategory(item) {
      this.selected = item;
      this.categoryName = item.name;
      this.newSubName = "";
      this.subSearch = "";
      this.subOptions = { ...this.subOptions, page: 1 };
      this.dialog = true;
    },
    async insert(name, parentId) {
      if (!name || !name.trim()) {
        this.$toast.info("Debes escribir un nombre");
        return false;
      }
      if (this.nameExists(name, parentId)) {
        this.$toast.warning(`Ya existe "${name.trim()}"`);
        return false;
      }
      this.saving = true;
      try {
        const response = await this.api_Post("accounting/projects", { name: name.trim(), parent_id: parentId });
        if (response.status != 200) {
          this.$toast.error("Error al guardar");
          return false;
        }
        await this.init();
        return true;
      } catch (e) {
        this.$toast.error("Error " + e.message);
        return false;
      } finally {
        this.saving = false;
      }
    },
    async createCategory() {
      if (await this.insert(this.newCategoryName, null)) {
        this.$toast.success("Categoría creada");
        this.dialogNew = false;
      }
    },
    async createSubCategory() {
      if (await this.insert(this.newSubName, this.selected.id)) {
        this.$toast.success("Sub-categoría creada");
        this.newSubName = "";
      }
    },
    async update(item, name) {
      if (!name || !name.trim() || name.trim() === item.name) return false;
      if (this.nameExists(name, item.parent_id, item.id)) {
        this.$toast.warning(`Ya existe "${name.trim()}"`);
        return false;
      }
      this.saving = true;
      try {
        const response = await this.api_Put("accounting/projects", { id: item.id, name: name.trim() });
        if (response.status != 200) {
          this.$toast.error("Error al guardar");
          return false;
        }
        await this.init();
        this.$toast.success("Nombre actualizado");
        return true;
      } catch (e) {
        this.$toast.error("Error " + e.message);
        return false;
      } finally {
        this.saving = false;
      }
    },
    renameCategory() {
      this.update(this.selected, this.categoryName);
    },
    renameSubCategory(item) {
      this.update(item, this.editSubName);
    },
    askDelete(item) {
      if (item.parent_id == null && this.subCount(item.id) > 0) {
        this.$toast.warning("Elimina primero las sub-categorías de esta categoría");
        return;
      }
      this.toDelete = item;
      this.dialogDelete = true;
    },
    async deleteConfirm() {
      this.saving = true;
      try {
        const response = await this.api_Delete("accounting/categories/" + this.toDelete.id);
        if (response.status == 200) {
          this.$toast.success("Eliminado");
          await this.init();
        } else {
          const body = await response.json().catch(() => ({}));
          this.$toast.error(body.error || "No se pudo eliminar");
        }
      } catch (e) {
        this.$toast.error("Error " + e.message);
      } finally {
        this.saving = false;
        this.dialogDelete = false;
        this.toDelete = null;
      }
    },
  },
};
</script>
